// Admin → Photo History
//
// Read-only chronological view of every photo recovery and deletion event
// across all field-report forms. Joins db.photo_recovery_log and
// db.photo_deletion_log via the backend endpoint
// GET /api/admin/photo-history/scan, with filters for job number, user,
// and date range.
//
// NO writes. Pairs with /admin/photo-audit (the scanner) and
// /admin/photo-audit/recover (the merger).

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Camera, History, ShieldCheck, Search, RefreshCw, Download,
  ArrowDownCircle, ArrowUpCircle, X, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';


export default function AdminPhotoHistory() {
  const [filters, setFilters] = useState({ job_number: '', user: '', from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('fieldflow_token')}` },
  });

  const runScan = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.job_number) params.job_number = filters.job_number.trim();
      if (filters.user)       params.user       = filters.user.trim();
      if (filters.from)       params.from       = filters.from;
      if (filters.to)         params.to         = filters.to;
      const r = await axios.get(`${API}/admin/photo-history/scan`, { ...authHeaders(), params });
      setReport(r.data);
      const t = r.data.totals;
      toast.success(`${t.events} event${t.events === 1 ? '' : 's'} (${t.recoveries} recovered, ${t.deletions} deleted)`);
    } catch (e) {
      toast.error(`Scan failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Initial unfiltered load on mount.
  useEffect(() => { runScan(); /* eslint-disable-next-line */ }, []);

  const clear = () => setFilters({ job_number: '', user: '', from: '', to: '' });

  const downloadCsv = () => {
    if (!report?.events?.length) return;
    const rows = [
      ['at', 'event_type', 'form_type', 'job_number', 'form_id', 'photo_id', 'onedrive_id', 'by', 'caption_or_storage'],
      ...report.events.map(e => [
        e.at || '', e.event_type, e.form_type, e.job_number || '', e.form_id,
        e.photo_id, e.onedrive_id || '', e.by || '', e.caption || e.storage || '',
      ]),
    ];
    const csv = rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `photo-history-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-7xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/admin/photo-audit" className="text-blue-600 hover:underline flex items-center gap-1 text-sm" data-testid="back-to-audit">
            <ArrowLeft className="w-4 h-4" /> Back to audit
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <History className="w-6 h-6 text-blue-600" /> Photo History
            </h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Every photo recovery and deletion across PreCon, Locate, and Soft Dig forms.
            </p>
          </div>
        </div>
        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 px-3 py-1">
          <ShieldCheck className="w-3 h-3 mr-1" /> Read-only
        </Badge>
      </div>

      {/* Filters */}
      <Card data-testid="photo-history-filters">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <FilterField label="Job number">
              <Input
                value={filters.job_number}
                onChange={e => setFilters(f => ({ ...f, job_number: e.target.value }))}
                placeholder="e.g. J20260521-002"
                data-testid="filter-job-number"
              />
            </FilterField>
            <FilterField label="User (email substring)">
              <Input
                value={filters.user}
                onChange={e => setFilters(f => ({ ...f, user: e.target.value }))}
                placeholder="e.g. chanhoyle"
                data-testid="filter-user"
              />
            </FilterField>
            <FilterField label="From (date)">
              <Input
                type="date"
                value={filters.from}
                onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
                data-testid="filter-from"
              />
            </FilterField>
            <FilterField label="To (date)">
              <Input
                type="date"
                value={filters.to}
                onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
                data-testid="filter-to"
              />
            </FilterField>
          </div>
          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Button onClick={runScan} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-filters-btn">
              {loading ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading…</>) : (<><Search className="w-4 h-4 mr-2" /> Apply</>)}
            </Button>
            <Button variant="outline" onClick={() => { clear(); setTimeout(runScan, 50); }} disabled={loading} data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-2" /> Clear
            </Button>
            {report?.events?.length > 0 && (
              <Button variant="outline" onClick={downloadCsv} data-testid="download-csv-btn">
                <Download className="w-4 h-4 mr-2" /> Download CSV
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {report && (
        <Card data-testid="photo-history-summary">
          <CardContent className="py-4">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Total events"      value={report.totals.events}      tone="slate"   testid="stat-events" />
              <Stat label="Recoveries"        value={report.totals.recoveries}  tone="emerald" testid="stat-recoveries" />
              <Stat label="Deletions"         value={report.totals.deletions}   tone="red"     testid="stat-deletions" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Events ({report?.events?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!report ? (
            <p className="text-sm text-slate-500 py-6 text-center">Loading…</p>
          ) : report.events.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No events match these filters. Try clearing them.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="photo-history-table">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-left">
                    <th className="py-2 pr-3 font-medium">When</th>
                    <th className="py-2 pr-3 font-medium">Event</th>
                    <th className="py-2 pr-3 font-medium">Form</th>
                    <th className="py-2 pr-3 font-medium">Job #</th>
                    <th className="py-2 pr-3 font-medium">Photo ID</th>
                    <th className="py-2 pr-3 font-medium">OneDrive ID</th>
                    <th className="py-2 pr-3 font-medium">By</th>
                    <th className="py-2 pr-3 font-medium">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {report.events.map((e, i) => <EventRow key={`${e.event_type}-${e.photo_id}-${e.at}-${i}`} e={e} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function EventRow({ e }) {
  const when = e.at ? new Date(e.at).toLocaleString() : '—';
  const isRecover = e.event_type === 'recovered';
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50">
      <td className="py-2 pr-3 text-xs whitespace-nowrap">{when}</td>
      <td className="py-2 pr-3">
        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded ${isRecover ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
          {isRecover ? <ArrowUpCircle className="w-3 h-3" /> : <ArrowDownCircle className="w-3 h-3" />}
          {isRecover ? 'recovered' : 'deleted'}
        </span>
      </td>
      <td className="py-2 pr-3 text-xs font-mono text-slate-600">{e.form_type}</td>
      <td className="py-2 pr-3 text-xs font-mono">{e.job_number || '—'}</td>
      <td className="py-2 pr-3 text-xs font-mono text-slate-500" title={e.photo_id}>{(e.photo_id || '').slice(0, 8)}…</td>
      <td className="py-2 pr-3 text-xs font-mono text-slate-500" title={e.onedrive_id || ''}>
        {e.onedrive_id ? `${e.onedrive_id.slice(0, 12)}…` : '—'}
      </td>
      <td className="py-2 pr-3 text-xs">{e.by || '—'}</td>
      <td className="py-2 pr-3 text-xs text-slate-500">{e.caption || e.storage || ''}</td>
    </tr>
  );
}


function FilterField({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
      {children}
    </div>
  );
}


function Stat({ label, value, tone = 'slate', testid }) {
  const tones = {
    slate:   'bg-slate-50 text-slate-900 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
    red:     'bg-red-50 text-red-900 border-red-200',
  }[tone];
  return (
    <div className={`rounded-md border px-3 py-2 ${tones}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wide font-medium opacity-70">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
